import React, { useEffect, useRef, useState } from 'react';
import { Palette, Heart, Sparkles, Share2 } from 'lucide-react';

const iconMap = { Palette, Heart, Sparkles, Share2 };
const colors = ['#007AFF', '#FF3B30', '#FF9500', '#34C759']; // Vibrant Blue, Red, Orange, Green

const steps = [
  { step: 1, title: "Choose Your Style", description: "Browse our template collection or tell us your vision for a custom design.", icon: "Palette" },
  { step: 2, title: "Share Your Story", description: "Send us your photos, love story, event details, and venue information.", icon: "Heart" },
  { step: 3, title: "We Craft It", description: "Our team designs and builds your personalized wedding invitation website.", icon: "Sparkles" },
  { step: 4, title: "Share & Celebrate", description: "Get your unique link and share your beautiful WedSite with everyone.", icon: "Share2" },
];

export const HowItWorks = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 lg:py-32 bg-white relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" ref={ref}>
        {/* Header */}
        <div className={`text-center max-w-2xl mx-auto mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-block px-3 py-1 mb-4 text-xs font-semibold tracking-wider text-vibrant-purple uppercase bg-purple-50 rounded-full border border-purple-100">
            Process
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold leading-tight text-slate-900 mb-6 font-outfit">
            How It <span className="text-vibrant-purple">Works</span>
          </h2>
          <p className="text-lg text-slate-600">
            Four simple steps to get your dream wedding website.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connecting Line (Desktop) */}
          <div className="hidden lg:block absolute top-[60px] left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-blue-200 via-red-200 to-green-200" />

          {steps.map((item, index) => {
            const Icon = iconMap[item.icon];
            const color = colors[index];
            return (
              <div
                key={item.step}
                className={`relative flex flex-col items-center text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Icon Circle */}
                <div
                  className="z-10 w-24 h-24 rounded-full bg-white border-4 flex items-center justify-center mb-6 shadow-xl transition-transform duration-300 hover:scale-110 group"
                  style={{ borderColor: `${color}30` }} // 30% opacity border
                >
                  <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: `${color}15` }}>
                    <Icon className="w-9 h-9 transition-colors duration-300" style={{ color: color }} />
                  </div>
                </div>

                {/* Step Number Badge */}
                <div
                  className="absolute top-0 right-[calc(50%-45px)] w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm border-2 border-white shadow-md z-20"
                >
                  {item.step}
                </div>

                <h3 className="text-xl font-bold mb-3 text-slate-900 font-outfit">
                  {item.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm px-4">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
